/* eslint-disable n/handle-callback-err */
import React, { useState, useEffect, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  MenuItem,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  CircularProgress,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { Save as SaveIcon, Search, LocationOn, PersonSearch, AssignmentTurnedIn } from "@mui/icons-material"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning, showSuccess } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Swal from "sweetalert2"

// ⭐ IMPORTACIÓN DEL MODAL DE DOCUMENTOS GLOBAL
import DocumentosAsociadosModal from "../../components/Global/DocumentosAsociadosModal/index"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1000px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EjecutarEncuesta = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const encuestaListada = state?.encuesta
  const enccodigo = encuestaListada?.enccodigo

  const [respuestas, setRespuestas] = useState({})
  const [cedulaBusqueda, setCedulaBusqueda] = useState("")
  const [fechaNacimientoBusqueda, setFechaNacimientoBusqueda] = useState("")
  const [nombresBusqueda, setNombresBusqueda] = useState("")
  const [provinciaBusqueda, setProvinciaBusqueda] = useState("")
  const [cantonBusqueda, setCantonBusqueda] = useState("")
  const [parroquiaBusqueda, setParroquiaBusqueda] = useState("")
  const [zonaBusqueda, setZonaBusqueda] = useState("")

  const [geo, setGeo] = useState({ lat: null, lng: null, error: "Obteniendo ubicación..." })

  const [savedRespcodigo, setSavedRespcodigo] = useState(null)
  const [isDocsModalOpen, setIsDocsModalOpen] = useState(false)

  // ⭐ ESTADOS MANUALES (BYPASS AL WRAPPER DE SIAC)
  const [isSearchingCNE, setIsSearchingCNE] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => setGeo({ lat: position.coords.latitude, lng: position.coords.longitude, error: "" }),
        (error) => setGeo({ lat: null, lng: null, error: "Ubicación denegada o inactiva" }),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      )
    } else {
      setGeo({ lat: null, lng: null, error: "El navegador no soporta geolocalización" })
    }
  }, [])

  const { data: encuestaData, isLoading: isLoadingEnc } = useQuery({
    queryKey: ["getEstructuraEncuesta", enccodigo],
    queryFn: async () => {
      if (!enccodigo) return null
      const res = await api.post("/Encuestas/getEncuestaCompleta", { enccodigo })
      const payload = res?.success !== undefined ? res : res?.data
      if (payload && payload.success) return payload.data
      return null
    },
    enabled: !!enccodigo,
    refetchOnWindowFocus: false,
  })

  // ==========================================================
  // BÚSQUEDA DIRECTA (Sin el Wrapper)
  // ==========================================================
  const handleBuscarCNE = async () => {
    if (!cedulaBusqueda || cedulaBusqueda.length < 10) {
      return Swal.fire({ icon: "warning", title: "Atención", text: "Digite una cédula válida de 10 dígitos" })
    }

    setIsSearchingCNE(true)
    try {
      const res = await api.post("/EjecEncuestas/consultarCNE", { cedula: cedulaBusqueda })

      const isSuccess = res.success !== undefined ? res.success : res.data?.success
      const innerData = res.success !== undefined ? res.data : res.data?.data
      const message = res.message || res.data?.message

      if (isSuccess) {
        setNombresBusqueda(innerData.nombres || "")
        setProvinciaBusqueda(innerData.provincia?.includes("N/A") ? "" : innerData.provincia || "")
        setCantonBusqueda(innerData.canton === "N/A" ? "" : innerData.canton || "")
        setParroquiaBusqueda(innerData.parroquia === "N/A" ? "" : innerData.parroquia || "")
        setZonaBusqueda(innerData.zona === "N/A" ? "" : innerData.zona || "")

        try {
          if (innerData.fechaNacimiento) {
            const fechaStr = String(innerData.fechaNacimiento).trim()
            const fechaSplit = fechaStr.split(" ")[0]
            if (fechaSplit.includes("/")) {
              const parts = fechaSplit.split("/")
              if (parts.length === 3) {
                setFechaNacimientoBusqueda(`${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`)
              }
            }
          } else {
            setFechaNacimientoBusqueda("")
          }
        } catch (e) {
          setFechaNacimientoBusqueda("")
        }

        if (message) {
          Swal.fire({
            icon: "info",
            title: "Plan B Activado",
            text: message,
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          })
        } else {
          showSuccess("Datos obtenidos desde el Padrón Electoral")
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "No encontrado",
          text: message || "No se pudo consultar la cédula. Proceda a digitar manualmente.",
        })
      }
    } catch (err) {
      console.error("Error en consulta CNE/SRI:", err)
      Swal.fire({ icon: "error", title: "Error de Red", text: "Problemas de conectividad con el servidor." })
    } finally {
      setIsSearchingCNE(false)
    }
  }

  const handleRespuestaChange = (pregsecuen, tipo, valor) => {
    setRespuestas((prev) => {
      if (tipo === "M") {
        const actuales = prev[pregsecuen] || []
        const index = actuales.indexOf(valor)
        const nuevos = [...actuales]
        if (index === -1) nuevos.push(valor)
        else nuevos.splice(index, 1)
        return { ...prev, [pregsecuen]: nuevos }
      }
      return { ...prev, [pregsecuen]: valor }
    })
  }

  // ==========================================================
  // GUARDADO DIRECTO (Sin el Wrapper que se tragaba el JSON)
  // ==========================================================
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!encuestaData || !encuestaData.preguntas)
      return Swal.fire({ icon: "warning", title: "Atención", text: "La encuesta no tiene estructura válida." })

    const preguntas = encuestaData.preguntas
    const formateadas = []

    for (let i = 0; i < preguntas.length; i++) {
      const p = preguntas[i]
      const valor = respuestas[p.pregsecuen]

      if (p.pregobligatoria === 1) {
        if (p.pregtipo === "U" && (!valor || !valor.trim()))
          return Swal.fire({ icon: "warning", title: "Incompleto", text: `La pregunta #${i + 1} es obligatoria.` })
        if (p.pregtipo === "L" && !valor)
          return Swal.fire({
            icon: "warning",
            title: "Incompleto",
            text: `Seleccione una opción en la pregunta #${i + 1}.`,
          })
        if (p.pregtipo === "M" && (!valor || valor.length === 0))
          return Swal.fire({
            icon: "warning",
            title: "Incompleto",
            text: `Seleccione al menos una casilla en la pregunta #${i + 1}.`,
          })
      }

      if (valor) {
        if (p.pregtipo === "U") formateadas.push({ pregsecuen: p.pregsecuen, opcsecuen: 0, resptextolibre: valor })
        else if (p.pregtipo === "L")
          formateadas.push({ pregsecuen: p.pregsecuen, opcsecuen: valor, resptextolibre: null })
        else if (p.pregtipo === "M")
          valor.forEach((v) => formateadas.push({ pregsecuen: p.pregsecuen, opcsecuen: v, resptextolibre: null }))
      }
    }

    setIsSaving(true)
    try {
      const payload = {
        enccodigo,
        audsecuen: encuestaListada.audsecuen || null,
        respcedula: cedulaBusqueda,
        respnombre: nombresBusqueda,
        respprovincia: provinciaBusqueda,
        respcanton: cantonBusqueda,
        respparroquia: parroquiaBusqueda,
        respzona: zonaBusqueda,
        resplatitud: geo.lat,
        resplongitud: geo.lng,
        respfecnac: fechaNacimientoBusqueda || null,
        respuestas: formateadas,
      }

      // Llamada directa sin mutaciones intermedias
      const res = await api.post("/EjecEncuestas/saveRespuestaEncuesta", payload)

      // Extracción blindada del JSON
      const responseData = res?.data !== undefined ? res.data : res

      if (responseData && responseData.success) {
        setSavedRespcodigo(responseData.respcodigo)

        // ¡Alerta con el comprobante exacto!
        Swal.fire({
          title: "¡Encuesta Procesada!",
          text: `Se generó el comprobante ${responseData.respcodigo}. ¿Desea adjuntar fotos o evidencias?`,
          icon: "success",
          showCancelButton: true,
          confirmButtonColor: "#196C87",
          cancelButtonColor: "#757575",
          confirmButtonText: "Sí, adjuntar fotos",
          cancelButtonText: "No, salir",
          allowOutsideClick: false,
        }).then((result) => {
          if (result.isConfirmed) setIsDocsModalOpen(true)
          else navigate(-1)
        })
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: responseData?.message || "Error al procesar la encuesta en el servidor.",
        })
      }
    } catch (error) {
      console.error("Error guardando:", error)
      Swal.fire({
        icon: "error",
        title: "Error de Red",
        text: "Problemas al guardar la ejecución. Verifique su conexión.",
      })
    } finally {
      setIsSaving(false)
    }
  }

  const ejecutarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) => action?.acccaption === "GRABAR" || action?.acccaption === "EJECUTAR",
  )
  const toolbarActions = ejecutarAction
    ? [
        {
          label: ejecutarAction.acccaption,
          key: ejecutarAction.acccaption,
          icon: getIconComponent(ejecutarAction.accnameicono, ejecutarAction.acctipoico),
        },
      ]
    : [{ label: "FINALIZAR ENCUESTA", key: "GRABAR", icon: <SaveIcon /> }]

  if (!encuestaListada) {
    return (
      <ThemeProvider theme={theme}>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <BackIcon />
          <Alert severity="error" sx={{ mt: 3 }}>
            Regrese a la bandeja.
          </Alert>
        </div>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <span>
                <IconButton
                  onClick={handleSubmit}
                  disabled={isSaving || isSearchingCNE || isLoadingEnc}
                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "white" }}
                >
                  {action.icon}
                </IconButton>
              </span>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 20px 30px",
            fontSize: "25px",
            color: "#196C87",
          }}
        >
          <AssignmentTurnedIn sx={{ fontSize: 32, mr: 1 }} />
          <b>Ejecución: {encuestaListada.enctitulo}</b>
        </div>

        <CustomBackdrop isLoading={isSaving || isLoadingEnc} />

        <Box sx={StyledRoot} component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={6}>
              <Alert icon={<LocationOn />} severity={geo.lat ? "success" : "warning"} sx={{ borderRadius: 2 }}>
                {geo.lat ? `Coordenadas capturadas: ${geo.lat.toFixed(5)}, ${geo.lng.toFixed(5)}` : geo.error}
              </Alert>
            </Grid>
            <Grid item xs={12} md={6}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Encuesta {encuestaListada.encpublica === 1 ? "Pública (Libre)" : "Asignada Específicamente"}
              </Alert>
            </Grid>
          </Grid>

          <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2, borderLeft: "5px solid #196C87", bgcolor: "white" }}>
            <Typography
              variant="subtitle1"
              color="primary"
              fontWeight="bold"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <PersonSearch sx={{ mr: 1 }} /> Datos del Encuestado
            </Typography>

            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  fullWidth
                  label="Cédula de Identidad *"
                  size="small"
                  value={cedulaBusqueda}
                  onChange={(e) => setCedulaBusqueda(e.target.value.replace(/\D/g, ""))}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Nacimiento"
                  size="small"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={fechaNacimientoBusqueda}
                  onChange={(e) => setFechaNacimientoBusqueda(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBuscarCNE}
                  fullWidth
                  disabled={isSearchingCNE || cedulaBusqueda.length < 10}
                  sx={{ height: 40 }}
                >
                  {isSearchingCNE ? <CircularProgress size={20} color="inherit" /> : "BUSCAR"}
                </Button>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombres Completos *"
                  size="small"
                  value={nombresBusqueda}
                  onChange={(e) => setNombresBusqueda(e.target.value.toUpperCase())}
                  sx={{ bgcolor: "#e8f5e9" }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Provincia"
                  size="small"
                  value={provinciaBusqueda}
                  onChange={(e) => setProvinciaBusqueda(e.target.value.toUpperCase())}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Cantón"
                  size="small"
                  value={cantonBusqueda}
                  onChange={(e) => setCantonBusqueda(e.target.value.toUpperCase())}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Parroquia"
                  size="small"
                  value={parroquiaBusqueda}
                  onChange={(e) => setParroquiaBusqueda(e.target.value.toUpperCase())}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Zona / Recinto"
                  size="small"
                  value={zonaBusqueda}
                  onChange={(e) => setZonaBusqueda(e.target.value.toUpperCase())}
                />
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
            Cuestionario
          </Typography>

          {encuestaData?.preguntas?.map((preg, index) => {
            const opciones = encuestaData.opciones.filter((o) => o.pregsecuen === preg.pregsecuen)
            const valorActual = respuestas[preg.pregsecuen] || (preg.pregtipo === "M" ? [] : "")

            return (
              <Card
                key={preg.pregsecuen}
                elevation={1}
                sx={{ mb: 3, borderLeft: preg.pregobligatoria === 1 ? "4px solid #f57c00" : "4px solid #4caf50" }}
              >
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    {index + 1}. {preg.pregtexto}{" "}
                    {preg.pregobligatoria === 1 && <span style={{ color: "red" }}>*</span>}
                  </Typography>

                  {preg.pregtipo === "U" && (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Escriba la respuesta aquí..."
                      value={valorActual}
                      onChange={(e) => handleRespuestaChange(preg.pregsecuen, "U", e.target.value)}
                    />
                  )}

                  {preg.pregtipo === "L" && (
                    <TextField
                      select
                      fullWidth
                      label="Seleccione una opción"
                      value={valorActual}
                      onChange={(e) => handleRespuestaChange(preg.pregsecuen, "L", e.target.value)}
                    >
                      <MenuItem value="">
                        <em>-- Seleccionar --</em>
                      </MenuItem>
                      {opciones.map((o) => (
                        <MenuItem key={o.opcsecuen} value={o.opcsecuen}>
                          {o.opctexto}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}

                  {preg.pregtipo === "M" && (
                    <FormGroup>
                      <Grid container spacing={1}>
                        {opciones.map((o) => (
                          <Grid item xs={12} sm={6} key={o.opcsecuen}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={Array.isArray(valorActual) && valorActual.includes(o.opcsecuen)}
                                  onChange={() => handleRespuestaChange(preg.pregsecuen, "M", o.opcsecuen)}
                                  color="primary"
                                />
                              }
                              label={o.opctexto}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </FormGroup>
                  )}
                </CardContent>
              </Card>
            )
          })}

          <Box mt={4} textAlign="center">
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              disabled={isSaving || isSearchingCNE}
              startIcon={<SaveIcon />}
              sx={{ minWidth: 200, py: 1.5, borderRadius: 2 }}
            >
              FINALIZAR Y ENVIAR ENCUESTA
            </Button>
          </Box>
        </Box>
      </div>

      <DocumentosAsociadosModal
        isOpen={isDocsModalOpen}
        onClose={() => {
          setIsDocsModalOpen(false)
          navigate(-1)
        }}
        contexto={{ docqgenero: savedRespcodigo, docprocqgenero: "enccrespuestas", docsecuen: 1 }}
        onSuccess={() => console.log("Fotos guardadas")}
      />
    </ThemeProvider>
  )
}

export default EjecutarEncuesta
